import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, Tag, ArrowRight, ShoppingBag, Truck, AlertCircle } from 'lucide-react';
import { cartAPI, authAPI } from './services/api';
import { useTranslation } from 'react-i18next';
import Header from './components/Header';

const ShoppingCartPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    totalItems: 0,
    selectedItems: 0,
    subtotal: 0,
    discount: 0,
    shipping: 0,
    total: 0
  });
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  // ✨ 載入時恢復優惠券（無論登入與否）
  useEffect(() => {
    loadCart();
    
    // ✨ 從 localStorage 載入保存的優惠券
    const savedCoupon = localStorage.getItem('applied_coupon');
    if (savedCoupon) {
      try {
        setAppliedCoupon(JSON.parse(savedCoupon));
      } catch (e) {
        console.error('優惠券解析失敗:', e);
        localStorage.removeItem('applied_coupon');
      }
    }
  }, []);

  // ✨ 載入購物車
  const loadCart = async () => {
    try {
      setLoading(true);
      const data = await cartAPI.getCart();
      console.log('API Response:', data);
      
      setCartItems(data.items || []);
      
    } catch (error) {
      console.error('載入購物車失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  // 計算購物車總計（包含優惠券）
  useEffect(() => {
    if (!cartItems || cartItems.length === 0) {
      setSummary({
        totalItems: 0,
        selectedItems: 0,
        subtotal: 0,
        discount: 0,
        shipping: 0,
        total: 0
      });
      return;
    }

    const selectedItems = cartItems.filter(item => item.selected);
    const totalItems = cartItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const selectedCount = selectedItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
    
    // 使用 parseFloat 確保數值運算正確
    const subtotal = selectedItems.reduce((sum, item) => {
      const price = parseFloat(item.price) || 0;
      const quantity = parseInt(item.quantity) || 0;
      return sum + (price * quantity);
    }, 0);

    // ✨ 計算優惠券折扣
    let discount = 0;
    if (appliedCoupon) {
      if (appliedCoupon.type === 'percentage') {
        discount = subtotal * (appliedCoupon.value / 100);
      } else if (appliedCoupon.type === 'fixed') {
        discount = appliedCoupon.value;
      }
      // 確保折扣不超過小計
      discount = Math.min(discount, subtotal);
    }

    const shipping = subtotal >= 500 ? 0 : 60;
    const total = Math.max(0, subtotal - discount + shipping);

    setSummary({
      totalItems,
      selectedItems: selectedCount,
      subtotal: parseFloat(subtotal.toFixed(2)),
      discount: parseFloat(discount.toFixed(2)),
      shipping,
      total: parseFloat(total.toFixed(2))
    });
  }, [cartItems, appliedCoupon]);

  // 更新商品數量
  const updateQuantity = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;

    try {
      await cartAPI.updateQuantity(itemId, newQuantity);
      setCartItems(prev => 
        prev.map(item => 
          item.id === itemId ? { ...item, quantity: newQuantity } : item
        )
      );
    } catch (error) {
      console.error('更新數量失敗:', error);
      alert('更新數量失敗，請稍後再試');
    }
  };

  // 刪除商品
  const removeItem = async (itemId) => {
    if (!window.confirm('確定要刪除這個商品嗎？')) return;

    try {
      await cartAPI.removeItem(itemId);
      setCartItems(prev => prev.filter(item => item.id !== itemId));
    } catch (error) {
      console.error('刪除商品失敗:', error);
      alert('刪除商品失敗，請稍後再試');
    }
  };

  // 切換商品選中狀態
  const toggleItemSelection = async (itemId) => {
    try {
      const item = cartItems.find(i => i.id === itemId);
      const newSelected = !item.selected;
      
      await cartAPI.updateSelection(itemId, newSelected);
      
      setCartItems(prev =>
        prev.map(i =>
          i.id === itemId ? { ...i, selected: newSelected } : i
        )
      );
    } catch (error) {
      console.error('更新選中狀態失敗:', error);
    }
  };

  // 全選/取消全選
  const toggleSelectAll = async () => {
    const allSelected = cartItems.every(item => item.selected);
    const newSelected = !allSelected;

    try {
      await cartAPI.updateAllSelection(newSelected);
      setCartItems(prev => prev.map(item => ({ ...item, selected: newSelected })));
    } catch (error) {
      console.error('批量更新選中狀態失敗:', error);
    }
  };

  // 刪除選中的商品
  const deleteSelected = async () => {
    const selectedItems = cartItems.filter(item => item.selected);
    if (selectedItems.length === 0) {
      alert('請先選擇要刪除的商品');
      return;
    }

    if (!window.confirm(`確定要刪除 ${selectedItems.length} 個商品嗎？`)) return;

    try {
      await cartAPI.removeSelectedItems();
      setCartItems(prev => prev.filter(item => !item.selected));
    } catch (error) {
      console.error('批量刪除失敗:', error);
      alert('刪除失敗，請稍後再試');
    }
  };

  // ✨ 應用優惠券（支援未登入）
  const applyCoupon = () => {
    if (!couponCode.trim()) {
      alert('請輸入優惠券代碼');
      return;
    }

    // 模擬優惠券驗證
    const validCoupons = {
      'SAVE10': { code: 'SAVE10', type: 'percentage', value: 10, description: '9折優惠' },
      'SAVE50': { code: 'SAVE50', type: 'fixed', value: 50, description: '折扣 TWD 50' },
      'FREESHIP': { code: 'FREESHIP', type: 'shipping', value: 0, description: '免運費' }
    };

    const coupon = validCoupons[couponCode.toUpperCase()];
    if (coupon) {
      setAppliedCoupon(coupon);
      // ✨ 保存優惠券到 localStorage
      localStorage.setItem('applied_coupon', JSON.stringify(coupon));
      alert(`✅ 優惠券「${coupon.code}」已成功應用！`);
      setCouponCode('');
    } else {
      alert('❌ 無效的優惠券代碼');
    }
  };

  // ✨ 移除優惠券
  const removeCoupon = () => {
    setAppliedCoupon(null);
    localStorage.removeItem('applied_coupon');
  };

  // 結帳
  const handleCheckout = () => {
    const selectedItems = cartItems.filter(item => item.selected);
    if (selectedItems.length === 0) {
      alert('請選擇要結帳的商品');
      return;
    }

    // 檢查是否登入
    if (!authAPI.isAuthenticated()) {
      if (window.confirm('結帳需要登入，是否前往登入頁面？')) {
        navigate('/auth');
      }
      return;
    }

    // TODO: 實現結帳流程
    alert('結帳功能開發中...');
  };

  const allSelected = cartItems.length > 0 && cartItems.every(item => item.selected);

  // 渲染商品圖片
  const renderProductImage = (item) => {
    if (item.imageUrl && (item.imageUrl.startsWith('data:image') || item.imageUrl.startsWith('http'))) {
      return (
        <img 
          src={item.imageUrl} 
          alt={item.productName}
          className="w-full h-full object-cover"
        />
      );
    }
    return <span className="text-5xl">{item.imageUrl || '📦'}</span>;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ✅ 頂部導航 - 使用 simple variant */}
      <Header variant="simple" title={t('cart.title') || '購物車'} />

      <div className="container mx-auto px-4 py-8">
        {loading ? (
          /* 載入中 */
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <div className="text-6xl mb-4">⏳</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">載入中...</h2>
            <p className="text-gray-600">正在載入您的購物車</p>
          </div>
        ) : cartItems.length === 0 ? (
          /* 空購物車 */
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <div className="text-8xl mb-4">🛒</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">您的購物車是空的</h2>
            <p className="text-gray-600 mb-6">快去添加一些商品吧!</p>
            
            {/* ✨ 未登入提示 */}
            {!authAPI.isAuthenticated() && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-blue-700 mb-2">💡 提示：登入後可以同步購物車到所有設備</p>
                <button
                  onClick={() => navigate('/auth')}
                  className="text-blue-600 hover:text-blue-700 font-semibold underline"
                >
                  立即登入
                </button>
              </div>
            )}
            
            <button
              onClick={() => navigate('/products')}
              className="inline-block bg-orange-500 text-white px-8 py-3 rounded-lg font-bold hover:bg-orange-600 transition cursor-pointer"
            >
              開始購物
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-6">
            {/* ✨ 未登入警告橫幅 */}
            {!authAPI.isAuthenticated() && (
              <div className="col-span-12 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <AlertCircle className="text-blue-500" size={24} />
                  <div className="flex-1">
                    <p className="font-semibold text-blue-800">您尚未登入</p>
                    <p className="text-sm text-blue-600">購物車數據僅保存在本地。登入後可以同步到所有設備。</p>
                  </div>
                  <button
                    onClick={() => navigate('/auth')}
                    className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition font-semibold"
                  >
                    立即登入
                  </button>
                </div>
              </div>
            )}

            {/* 左側購物車列表 */}
            <div className="col-span-8">
              {/* 全選和操作欄 */}
              <div className="bg-white rounded-lg shadow-md p-4 mb-4">
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      className="w-5 h-5 text-orange-500"
                    />
                    <span className="font-semibold">全選 ({cartItems.length} 件商品)</span>
                  </label>
                  <div className="flex items-center gap-4 text-sm">
                    <button 
                      onClick={deleteSelected}
                      className="text-gray-600 hover:text-orange-500"
                    >
                      刪除選中的商品
                    </button>
                  </div>
                </div>
              </div>

              {/* 商品列表 - 按賣家分組 */}
              <div className="space-y-4">
                {Object.entries(
                  cartItems.reduce((groups, item) => {
                    const seller = item.sellerName || item.seller_name || item.seller?.name || '官方旗艦店';
                    if (!groups[seller]) groups[seller] = [];
                    groups[seller].push(item);
                    return groups;
                  }, {})
                ).map(([sellerName, items]) => (
                  <div key={sellerName} className="bg-white rounded-lg shadow-md overflow-hidden">
                    {/* 賣家標題 */}
                    <div className="bg-gray-50 px-6 py-3 border-b flex items-center gap-2">
                      <ShoppingBag size={18} className="text-orange-500" />
                      <span className="font-semibold">{sellerName}</span>
                    </div>

                    {/* 商品列表 */}
                    <div className="divide-y">
                      {items.map((item) => (
                        <div key={item.id} className="p-6 hover:bg-gray-50 transition">
                          <div className="flex gap-4">
                            {/* 選擇框 */}
                            <input
                              type="checkbox"
                              checked={item.selected || false}
                              onChange={() => toggleItemSelection(item.id)}
                              className="w-5 h-5 text-orange-500 mt-1"
                            />

                            {/* 商品圖片 */}
                            <div 
                              className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden cursor-pointer"
                              onClick={() => navigate(`/product/${item.productId}`)}
                            >
                              {renderProductImage(item)}
                            </div>

                            {/* 商品資訊 */}
                            <div className="flex-1">
                              <h3 
                                className="font-semibold text-gray-800 mb-1 hover:text-orange-500 cursor-pointer"
                                onClick={() => navigate(`/product/${item.productId}`)}
                              >
                                {item.productName}
                              </h3>
                              {item.variant && item.variant !== '默認規格' && (
                                <p className="text-sm text-gray-500 mb-2">{item.variant}</p>
                              )}
                              <div className="flex items-center gap-4">
                                <span className="text-xl font-bold text-orange-500">
                                  TWD {parseFloat(item.price).toFixed(0)}
                                </span>
                                {item.originalPrice && parseFloat(item.originalPrice) > parseFloat(item.price) && (
                                  <span className="text-sm text-gray-400 line-through">
                                    TWD {parseFloat(item.originalPrice).toFixed(0)}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* 數量控制 */}
                            <div className="flex flex-col items-end justify-between">
                              <button
                                onClick={() => removeItem(item.id)}
                                className="text-gray-400 hover:text-red-500 transition"
                              >
                                <Trash2 size={18} />
                              </button>

                              <div className="flex items-center gap-2 border rounded-lg">
                                <button
                                  onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                  disabled={item.quantity <= 1}
                                  className="p-2 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                  <Minus size={16} />
                                </button>
                                <span className="w-12 text-center font-medium">{item.quantity}</span>
                                <button
                                  onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                  disabled={item.quantity >= (item.stock || 99)}
                                  className="p-2 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                  <Plus size={16} />
                                </button>
                              </div>

                              <div className="text-right">
                                <div className="text-sm text-gray-500">小計</div>
                                <div className="text-lg font-bold text-gray-900">
                                  TWD {(parseFloat(item.price) * item.quantity).toFixed(0)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 右側結算欄 */}
            <div className="col-span-4">
              <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
                <h2 className="text-xl font-bold mb-4">訂單摘要</h2>

                {/* 優惠券輸入 */}
                <div className="mb-6 pb-6 border-b">
                  <div className="flex items-center gap-2 mb-3">
                    <Tag className="text-orange-500" size={20} />
                    <span className="font-semibold">優惠券</span>
                  </div>
                  
                  {appliedCoupon ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-semibold text-green-800">{appliedCoupon.code}</div>
                          <div className="text-sm text-green-600">{appliedCoupon.description}</div>
                        </div>
                        <button
                          onClick={removeCoupon}
                          className="text-red-500 hover:text-red-600"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                        placeholder="輸入優惠券代碼"
                        className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                        onKeyPress={(e) => e.key === 'Enter' && applyCoupon()}
                      />
                      <button
                        onClick={applyCoupon}
                        className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition"
                      >
                        使用
                      </button>
                    </div>
                  )}
                  
                  <div className="mt-2 text-xs text-gray-500">
                    試試: SAVE10, SAVE50, FREESHIP
                  </div>
                </div>

                {/* 費用明細 */}
                <div className="space-y-3 mb-6 pb-6 border-b">
                  <div className="flex justify-between text-gray-600">
                    <span>商品總計 ({summary.selectedItems} 件)</span>
                    <span>TWD {summary.subtotal.toFixed(0)}</span>
                  </div>
                  
                  {summary.discount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>優惠折扣</span>
                      <span>-TWD {summary.discount.toFixed(0)}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between text-gray-600">
                    <div className="flex items-center gap-2">
                      <Truck size={16} />
                      <span>運費</span>
                    </div>
                    <span>
                      {summary.shipping === 0 ? (
                        <span className="text-green-600 font-semibold">免運費</span>
                      ) : (
                        `TWD ${summary.shipping}`
                      )}
                    </span>
                  </div>

                  {summary.subtotal > 0 && summary.subtotal < 500 && (
                    <div className="text-xs text-gray-500">
                      💡 再買 TWD {(500 - summary.subtotal).toFixed(0)} 即可免運費
                    </div>
                  )}
                </div>

                {/* 總計 */}
                <div className="flex justify-between items-center text-xl font-bold mb-6">
                  <span>總計</span>
                  <span className="text-orange-500">TWD {summary.total.toFixed(0)}</span>
                </div>

                {/* 結帳按鈕 */}
                <button
                  onClick={handleCheckout}
                  disabled={summary.selectedItems === 0}
                  className="w-full bg-orange-500 text-white py-3 rounded-lg font-bold hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <span>前往結帳</span>
                  <ArrowRight size={20} />
                </button>

                {/* 安全提示 */}
                <div className="mt-4 text-center text-sm text-gray-500">
                  🔒 安全結帳 | 💳 多種支付方式
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShoppingCartPage;